"use client";

import { useMemo } from "react";
import {
  useController,
  useWatch,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import {
  BackendSingleSelect,
  type BackendSingleSelectProps,
} from "./backend-single-select";
import type {
  ValidationErrorT,
  ValidationPopoverBgT,
} from "@/app/components/ui/validation-popover";

/**
 * Props for ControlledSingleSelect component
 */
export interface ControlledSingleSelectProps<
  TFieldValues extends FieldValues,
> extends Omit<
  BackendSingleSelectProps,
  "selectedValue" | "onSelectionChange" | "error"
> {
  /** Name of the form field */
  name: FieldPath<TFieldValues>;
  /** Callback for field blur events */
  onFieldBlur?: (fieldName: string) => void;
  /** Background variant for Validation Popover */
  bgVariant?: ValidationPopoverBgT;
  /** Whether the component is read-only (makes it disabled) */
  readOnly?: boolean;
}

/**
 * ControlledSingleSelect - React Hook Form integrated single select
 *
 * Provides seamless integration with React Hook Form for single value selection.
 * Handles form state, validation, and error display automatically.
 *
 * Usage:
 * ```tsx
 * <ControlledSingleSelect<OfferFormData>
 *   name="make_id"
 *   label="Marke"
 *   options={makeOptions}
 *   onFieldBlur={onFieldBlur}
 *   required
 * />
 * ```
 */
export function ControlledSingleSelect<TFieldValues extends FieldValues>({
  name,
  onFieldBlur,
  bgVariant = "accent",
  ...props
}: ControlledSingleSelectProps<TFieldValues>) {
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
  });

  // ✅ Use useWatch for reactive updates after form.reset()
  const watchedValue = useWatch<TFieldValues>({
    name,
  });

  const handleSelectionChange = (selectedValue: string | null) => {
    onChange(selectedValue);
  };

  const handleBlur = () => {
    onBlur();
    onFieldBlur?.(name);
  };

  // ✅ Use watched value for reactive updates, fallback to field.value
  const currentValue = watchedValue || value;
  // Ensure value is properly typed as string | null
  const typedValue = typeof currentValue === "string" ? currentValue : null;

  const errorMessage = error?.message;
  const hasError = Boolean(errorMessage);
  const errorMessages = useMemo(() => {
    return errorMessage ? [errorMessage] : [];
  }, [errorMessage]);

  const shouldShowValidation = hasError && errorMessages.length > 0;

  const { className, ...selectProps } = props;

  return (
    <div className={className}>
      <BackendSingleSelect
        {...selectProps}
        className={className}
        selectedValue={typedValue}
        onSelectionChange={handleSelectionChange}
        onBlur={handleBlur}
        error={error?.message}
        validationMessages={
          shouldShowValidation
            ? (errorMessages as (string | ValidationErrorT)[])
            : undefined
        }
        validationBgVariant={bgVariant}
        disabled={props.disabled}
        readOnly={props.readOnly}
      />
    </div>
  );
}
